import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Chip,
  Tooltip,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
} from '@mui/material';
import {
  FlightTakeoff as FlightIcon,
  Schedule as DurationIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Add as AddIcon,
  AccessTime as HoursIcon,
} from '@mui/icons-material';
import DroneIcon from '../../components/DroneIcon';
import { useOrganization } from '../../context/OrganizationContext';
import { useAuth0 } from '@auth0/auth0-react';
import OrganizationLayout from '../../components/organization/OrganizationLayout';
import LogFlightDialog from '../../components/fleet/LogFlightDialog';
import { getFlightLogs, addFlightLog, updateFlightLog } from '../../network/apis/flightLog.api';
import { getOrganizationMembers } from '../../network/apis/organization.api';
import styles from '../../styles/Analytics.module.css';

const SORT_FIELDS = {
  flightDate: (a, b) => new Date(b.flightDate) - new Date(a.flightDate),
  flightDuration: (a, b) => b.flightDuration - a.flightDuration,
  drone: (a, b) => (a.drone?.model || '').localeCompare(b.drone?.model || ''),
  pilot: (a, b) => (a.pilot?.firstName || '').localeCompare(b.pilot?.firstName || ''),
};

function FlightLogs() {
  const { currentOrganization, userRole } = useOrganization();
  const { getAccessTokenSilently } = useAuth0();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allLogs, setAllLogs] = useState([]);
  const [drones, setDrones] = useState([]);
  const [pilots, setPilots] = useState([]);

  // Filters (client-side)
  const [search, setSearch] = useState('');
  const [droneFilter, setDroneFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Sorting
  const [sortField, setSortField] = useState('flightDate');
  const [sortDir, setSortDir] = useState('asc'); // 'asc' = newest first (DESC date)

  // Edit/create dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null); // null = create

  const canView = ['owner', 'admin', 'manager'].includes(userRole?.toLowerCase());

  // ─── Data fetching ──────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!currentOrganization?.organizationUuid || !canView) return;
    try {
      setLoading(true);
      setError('');
      const token = await getAccessTokenSilently();

      const [logsData, dronesData, membersData] = await Promise.all([
        getFlightLogs(token, currentOrganization.organizationUuid),
        fetch(
          `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/drones`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).then((r) => r.json()),
        getOrganizationMembers(token, currentOrganization.organizationUuid),
      ]);

      setAllLogs(logsData.flightLogs || []);
      setDrones(dronesData.drones || dronesData || []);

      // Map members to the shape LogFlightDialog expects: { id, firstName, lastName }
      const activeMembers = (membersData.members || [])
        .filter((m) => m.user && m.status?.toUpperCase() === 'ACTIVE')
        .map((m) => ({
          id: m.userId,
          firstName: m.user.firstName,
          lastName: m.user.lastName,
        }));
      setPilots(activeMembers);
    } catch (err) {
      setError(err.message || 'Failed to load flight logs');
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.organizationUuid, canView, getAccessTokenSilently]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Filtering + sorting (client-side) ──────────────────────────────────────

  const filteredLogs = useMemo(() => {
    const q = search.toLowerCase();
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate + 'T23:59:59') : null;

    let result = allLogs.filter((log) => {
      if (droneFilter && String(log.drone?.id) !== String(droneFilter)) return false;
      if (start && new Date(log.flightDate) < start) return false;
      if (end && new Date(log.flightDate) > end) return false;
      if (q) {
        const haystack = [
          log.drone?.nickname,
          log.drone?.manufacturer,
          log.drone?.model,
          log.pilot?.firstName,
          log.pilot?.lastName,
          log.location,
          log.purpose,
          log.notes,
          log.weatherConditions,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });

    const comparator = SORT_FIELDS[sortField] || SORT_FIELDS.flightDate;
    result = [...result].sort(comparator);
    if (sortDir === 'desc') result.reverse();
    return result;
  }, [allLogs, search, droneFilter, startDate, endDate, sortField, sortDir]);

  // ─── Summary stats ───────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const total = filteredLogs.length;
    const hours = filteredLogs.reduce((s, l) => s + parseFloat(l.flightDuration || 0), 0);
    const avg = total > 0 ? hours / total : 0;
    const uniqueDrones = new Set(filteredLogs.map((l) => l.drone?.id).filter(Boolean)).size;
    return { total, hours, avg, uniqueDrones };
  }, [filteredLogs]);

  // ─── Sort handler ────────────────────────────────────────────────────────────

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortProps = (field) => ({
    active: sortField === field,
    direction: sortField === field ? sortDir : 'asc',
    onClick: () => handleSort(field),
  });

  // ─── Dialog handlers ─────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingLog(null);
    setDialogOpen(true);
  };

  const openEdit = (log) => {
    setEditingLog(log);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingLog(null);
  };

  const handleSubmit = async (formData) => {
    const token = await getAccessTokenSilently();
    if (editingLog) {
      await updateFlightLog(token, currentOrganization.organizationUuid, editingLog.id, formData);
    } else {
      await addFlightLog(token, currentOrganization.organizationUuid, formData);
    }
    await fetchData(); // refresh the table
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const droneName = (log) =>
    log.drone
      ? log.drone.nickname || `${log.drone.manufacturer} ${log.drone.model}`
      : '—';

  const pilotName = (log) =>
    log.pilot ? `${log.pilot.firstName} ${log.pilot.lastName}` : '—';

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const clearFilters = () => {
    setSearch('');
    setDroneFilter('');
    setStartDate('');
    setEndDate('');
  };

  const hasFilters = search || droneFilter || startDate || endDate;

  // ─── Permission guard ────────────────────────────────────────────────────────

  if (!canView) {
    return (
      <OrganizationLayout>
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error">You don't have permission to view flight logs.</Alert>
        </Container>
      </OrganizationLayout>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <OrganizationLayout>
      <Container maxWidth="xl" sx={{ mt: isMobile ? 2 : 4, mb: 4, px: isMobile ? 2 : 3 }}>
        {/* Page header */}
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', mb: 3, gap: isMobile ? 2 : 0 }}>
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} color="text.primary">
              Flight Logs
            </Typography>
            <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ mt: 0.5 }}>
              Search, review and manage all recorded flights across your organisation
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={!isMobile && <AddIcon />}
            onClick={openCreate}
            sx={{ textTransform: 'none', fontWeight: 600 }}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
          >
            Log New Flight
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Summary cards */}
        <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: 3 }}>
          {[
            {
              label: 'Total Flights',
              value: stats.total,
              icon: <FlightIcon />,
              color: '#1976d2',
              bg: '#e3f2fd',
            },
            {
              label: 'Total Flight Hours',
              value: stats.hours.toFixed(2),
              icon: <HoursIcon />,
              color: '#2e7d32',
              bg: '#e8f5e9',
            },
            {
              label: 'Avg Duration',
              value: `${stats.avg.toFixed(2)} hrs`,
              icon: <DurationIcon />,
              color: '#f57c00',
              bg: '#fff3e0',
            },
            {
              label: 'Drones Used',
              value: stats.uniqueDrones,
              icon: <DroneIcon />,
              color: '#7b1fa2',
              bg: '#f3e5f5',
            },
          ].map((card) => (
            <Grid item xs={6} sm={3} key={card.label} sx={{ display: 'flex' }}>
              <Card className={styles.metricCard} elevation={0} sx={{ border: '1px solid #e0e0e0', width: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ p: isMobile ? 1.25 : 2, '&:last-child': { pb: isMobile ? 1.25 : 2 }, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.75 : 1, justifyContent: isMobile ? 'flex-start' : 'flex-start', alignItems: isMobile ? 'flex-start' : 'center' }}>
                    <Box
                      sx={{
                        width: isMobile ? 28 : 36,
                        height: isMobile ? 28 : 36,
                        borderRadius: 1,
                        bgcolor: card.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: card.color,
                        fontSize: isMobile ? '1rem' : '1.5rem',
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      {card.label}
                    </Typography>
                  </Box>
                  <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} sx={{ color: card.color, fontSize: isMobile ? '1.25rem' : '1.5rem' }}>
                    {loading ? '—' : card.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Filter bar */}
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: 2 }}>
          <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
            <Grid container spacing={isMobile ? 1.5 : 2} alignItems="center">
              {/* Text search */}
              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={isMobile ? "Search…" : "Search drone, pilot, location, purpose…"}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>

              {/* Drone dropdown */}
              <Grid item xs={12} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Drone</InputLabel>
                  <Select
                    value={droneFilter}
                    onChange={(e) => setDroneFilter(e.target.value)}
                    label="Drone"
                  >
                    <MenuItem value="">All drones</MenuItem>
                    {drones.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.nickname || `${d.manufacturer} ${d.model}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Date from */}
              <Grid item xs={6} sm={2} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="From"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Date to */}
              <Grid item xs={6} sm={2} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="To"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Clear button */}
              <Grid item xs={12} sm={1} md={2}>
                {hasFilters && (
                  <Button
                    size="small"
                    onClick={clearFilters}
                    startIcon={!isMobile && <ClearIcon />}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    fullWidth={isMobile}
                  >
                    Clear filters
                  </Button>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Results count */}
        {!loading && (
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
              Showing <strong>{filteredLogs.length}</strong> of <strong>{allLogs.length}</strong> logs
            </Typography>
            {hasFilters && (
              <Chip label="Filtered" size="small" color="primary" variant="outlined" sx={{ height: isMobile ? 18 : 24, fontSize: isMobile ? '0.65rem' : '0.75rem' }} />
            )}
          </Box>
        )}

        {/* Table / Mobile Cards */}
        {loading ? (
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress />
            </Box>
          </Card>
        ) : isMobile ? (
          /* Mobile card view */
          <Stack spacing={1.5}>
            {filteredLogs.length === 0 ? (
              <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {hasFilters
                      ? 'No flight logs match your filters.'
                      : 'No flight logs recorded yet. Click "Log New Flight" to add one.'}
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              filteredLogs.map((log) => (
                <Card key={log.id} variant="outlined" elevation={0}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    {/* Header: Date + Edit button */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                        {formatDate(log.flightDate)}
                      </Typography>
                      <IconButton size="small" onClick={() => openEdit(log)} sx={{ padding: '4px' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Drone + Duration */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        {droneName(log)}
                      </Typography>
                      <Chip
                        label={`${parseFloat(log.flightDuration).toFixed(2)} hrs`}
                        size="small"
                        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem', height: 20 }}
                      />
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    {/* Details grid */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 0.75, rowGap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        Pilot:
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                        {pilotName(log)}
                      </Typography>

                      {log.location && (
                        <>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            Location:
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.location}
                          </Typography>
                        </>
                      )}

                      {log.purpose && (
                        <>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            Purpose:
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.purpose}
                          </Typography>
                        </>
                      )}

                      {log.operationId && (
                        <>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            Operation:
                          </Typography>
                          <Box>
                            <Chip
                              label={`FLZ${String(log.operationId).padStart(8, '0')}`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Box>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Stack>
        ) : (
          /* Desktop table view */
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }} className={styles.tableCard}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fafafa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel {...sortProps('flightDate')}>Date</TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel {...sortProps('drone')}>Drone</TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel {...sortProps('pilot')}>Pilot</TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel {...sortProps('flightDuration')}>Duration (hrs)</TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Operation</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#999' }}>
                        {hasFilters
                          ? 'No flight logs match your filters.'
                          : 'No flight logs recorded yet. Click "Log New Flight" to add one.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLogs.map((log) => (
                      <TableRow
                        key={log.id}
                        hover
                        sx={{ '&:last-child td': { border: 0 } }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDate(log.flightDate)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {droneName(log)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {pilotName(log)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${parseFloat(log.flightDuration).toFixed(2)} hrs`}
                            size="small"
                            sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Tooltip title={log.location || ''} placement="top" arrow>
                            <span>{log.location || '—'}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Tooltip title={log.purpose || ''} placement="top" arrow>
                            <span>{log.purpose || '—'}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {log.operationId ? (
                            <Chip label={`FLZ${String(log.operationId).padStart(8, '0')}`} size="small" variant="outlined" />
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit" arrow>
                            <IconButton size="small" onClick={() => openEdit(log)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Container>

      {/* Log / Edit flight dialog */}
      <LogFlightDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSubmit}
        drones={drones}
        pilots={pilots}
        flightLog={editingLog}
        currentUserId={null}
      />
    </OrganizationLayout>
  );
}

export default FlightLogs;
